<template>
    <DFormRow
        :label="label"
        :is-required="required"
        :error="errors.selectedUpgrades"
    >
        <div
            style="display: flex; align-items: center; width: 100%;"
        >
            <VueSelect
                v-model="productValues"
                :multiple="multiple"
                :close-on-select="!multiple"
                :options="productsList"
                :filterable="false"
                :disabled="disabled"
                :clearable="false"
                @open="onOpen"
                @close="onClose"
                @search="updateSearchText"
                @option:selected="emitValue"
                @option:deselected="emitValue"
            >
                <template #list-footer>
                    <li
                        v-show="hasNextPage"
                        ref="load"
                        class="loader"
                    >
                        {{ __('messages.loading_more_options') }}
                    </li>
                </template>
                <template #no-options="{ searching }">
                    <template v-if="searching">
                        {{ __('products.products.form.no_results') }}
                    </template>
                    <em
                        v-else
                        style="opacity: 0.5"
                    >
                        {{ __('messages.start_typing') }}
                    </em>
                </template>
            </VueSelect>
            <div style="display: flex; align-items: center; flex-shrink: 0; margin-left: 10px">
                <DSwitch
                    id="switch-display-retired"
                    v-model="displayRetiredProducts"
                    color="success"
                    variant="3d"
                    :disabled="disabled"
                    @update:checked="handleRetiredChange"
                />
                <label style="margin-bottom: 0; padding-left: 5px;">
                    {{ __('products.products.form.also_display_retired_products') }}
                </label>
            </div>
        </div>
    </DFormRow>
</template>

<script>

import VueSelect from 'vue-select'

export default {
    name: 'ProductChangesSelection',
    components: { VueSelect },
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        multiple: {
            type: Boolean,
            default: true
        },
        productId: {
            type: Number,
            default: 0
        },
        selectedProducts: {
            type: Array,
            default: () => { return [] }
        },
        required: {
            type: Boolean,
            default: false,
            required: false
        },
        disabled: {
            type: Boolean,
            default: false,
            required: false
        },
        label: {
            type: String,
            default: ''
        },
        labelCol: {
            type: String,
            default: '2'
        },
        selectCol: {
            type: String,
            default: '6'
        },
        ignoreCurrent: {
            type: Boolean,
            default: true
        },
        availableProducts: {
            type: String,
            default: 'all'
        },
        fulfillmentModule: {
            type: String,
            default: null
        },
        fulfillmentAccountGroupId: {
            type: String,
            default: null
        },
        includeCurrentProduct: {
            type: Boolean,
            default: false
        },
        marketRegionId: {
            type: Number,
            default: null
        }
    },
    emits: ['update'],
    data () {
        return {
            displayRetiredProducts: false,
            productValues: this.selectedProducts,
            products: [],
            observer: null,
            searchText: null,
            currentPage: 1,
            lastPage: 1,
            timeout: null
        }
    },
    computed: {
        hasNextPage () {
            return this.currentPage < this.lastPage
        },
        productsList () {
            return this.products
        }
    },
    mounted () {
        this.observer = new IntersectionObserver(this.appendProducts)
    },
    created () {
        this.fetchProductsInitially()

        if (this.disabled) {
            this.products = this.selectedProducts
        }
    },
    updated () {
        this.fetchProductsInitially()
    },
    methods: {
        async onOpen () {
            if (this.hasNextPage) {
                await this.$nextTick()
                this.observer.observe(this.$refs.load)
            }
        },
        onClose () {
            this.observer.disconnect()
        },
        async fetchProductsInitially () {
            this.currentPage = 1

            await this.fetchProducts()
                .then((res) => {
                    this.lastPage = res.data.lastPage
                    this.products = res.data.items
                })
        },
        async appendProducts ([{ isIntersecting, target }]) {
            if (isIntersecting) {
                const ul = target.offsetParent
                const scrollTop = target.offsetParent.scrollTop

                this.currentPage++

                await this.fetchProducts()
                    .then((res) => {
                        this.products = this.products.concat(res.data.items)
                    })

                await this.$nextTick()
                ul.scrollTop = scrollTop
            }
        },
        fetchProducts () {
            const params = {
                product: this.productId ?? null,
                retired: this.displayRetiredProducts ? 1 : 0,
                page: this.currentPage,
                ignore_current: this.ignoreCurrent ? 1 : 0,
                available_products: this.availableProducts,
                fulfillment_module: this.fulfillmentModule,
                fulfillment_account_group_id: this.fulfillmentAccountGroupId,
                include_current_product: this.includeCurrentProduct,
                market_region_id: this.marketRegionId
            }

            if (this.searchText) {
                params.query = this.searchText
            }

            if (this.productId !== null) {
                return window.axios.get(this.$route('products.dropdown.upgradable_products', params))
            } else {
                return window.axios.get(this.$route('products.dropdown.upgradable_products_by_fulfillment', params))
            }
        },
        updateSearchText (query) {
            this.searchText = query

            if (this.timer) {
                clearTimeout(this.timer)

                this.timer = null
            }

            this.timer = setTimeout(() => {
                this.fetchProductsInitially()
            }, 500)
        },
        handleRetiredChange () {
            this.fetchProductsInitially()
        },
        emitValue () {
            this.$emit('update', this.productValues)
        }
    }
}
</script>

<style>
:root {
    --vs-selected-color: #5c6873 !important;
    --vs-state-disabled-bg: #e4e7ea !important;
    --vs-border-color: rgb(228, 231, 234) !important;
}
.v-select {
    flex-grow: 1
}
</style>
